/**
 * @file This file contains the custom handlers for the application.
 *
 * @remarks
 * This array should include all custom handlers that are not generated by Orval.
 *
 * @packageDocumentation
 */

import { coinvestHandlers } from "@vega-api/custom/msw/handlers/co-invest/coinvestHandlers";
import { getSubscriptionByIdHandler } from "@vega-api/custom/msw/handlers/subscription-templates/getSubscriptionById";
import { getSubscriptionTemplatesHandler } from "@vega-api/custom/msw/handlers/subscription-templates/getSubscriptionTemplates";
import { getTrackAnalyticsEventMockHandler } from "@vega-api/generated/analytics-events/analytics-events.msw";
import { getGetCountryListMockHandler } from "@vega-api/generated/country-v1/country-v1.msw";
import { getGetRedirectUrlMockHandler } from "@vega-api/generated/idp-v1/idp-v1.msw";
import { getRetoolFacadeStartWorkflowCommandMockHandler } from "@vega-api/generated/retool-facade-workflow-commands/retool-facade-workflow-commands.msw";
import { getGetCommentForSubscriptionMockHandler } from "@vega-api/generated/subscription-comments-v1/subscription-comments-v1.msw";

import countryList from "./countryList/countryList.json";
import { idManagerHandlers } from "./data-ingestion/id-manager/idManagerHandlers";
import { ingestionHandlers } from "./data-ingestion/ingestionHandlers";
import { fundHandlers } from "./fund/fundHandlers";
import {
  getCustomCaseAttentionHandler,
  getCustomCaseByIdHandler,
  getCustomCaseByNumberHandler,
  getCustomCaseCommentsHandler,
  getCustomCaseEmailsHandler,
  getCustomCasesHandler,
  getCustomCaseTypesHandler,
  getCustomProfileHandler,
  getCustomSavedFiltersHandler,
  getCustomUserByIdHandler,
} from "./gp-portal/case-management";
import { homeLayoutDemoHandlers } from "./gp-portal/home-layout";
import { quickLinksDemoHandlers } from "./gp-portal/quick-links";
import {
  getReportingApiKeysHandler,
  getWorkbenchInvestorByIdDemoHandler,
  getWorkbenchInvestorsDemoHandler,
} from "./gp-portal/reporting-api-keys";
import { getUserActivityStatsDemoHandler } from "./gp-portal/user-activity-stats";
import { notificationHubInsightsHandlers } from "./notification-hub/insightsHandlers";
import { notificationHubHandlers } from "./notification-hub/notificationHubHandlers";
import { reportingPageHandlers } from "./reporting/reportingHandlers";
import { getSubscriptionQuestionnaireV3MockExtended } from "./subscription-questionnaire-v3/subscription-questionnaire-v3.msw";
import {
  getAllFeatureFlagsHandler,
  getCustomFeatureFlagHandler,
  getDemoFeatureFlagHandler,
  getV1CheckHandler,
} from "./featureFlag";
import { getUiCapabilitiesHandler } from "./uiCapabilities";

export const customHandlers = [
  ...coinvestHandlers,
  ...getSubscriptionQuestionnaireV3MockExtended(),
  getGetCountryListMockHandler(countryList),
  getGetRedirectUrlMockHandler({ ssoUrl: "http://localhost:3010" }),
  getV1CheckHandler(),
  getCustomFeatureFlagHandler(),
  getAllFeatureFlagsHandler(),
  getUiCapabilitiesHandler(),
  getCustomCaseByNumberHandler(),
  getCustomCaseByIdHandler(),
  getCustomCaseTypesHandler(),
  getCustomProfileHandler(),
  getCustomCaseEmailsHandler(),
  getCustomCaseCommentsHandler(),
  getCustomUserByIdHandler(),
  getCustomCasesHandler(),
  getCustomCaseAttentionHandler(),
  getCustomSavedFiltersHandler(),
  getReportingApiKeysHandler(),
  getSubscriptionByIdHandler(),
  getSubscriptionTemplatesHandler(),
  getRetoolFacadeStartWorkflowCommandMockHandler(),
  ...fundHandlers,
  ...reportingPageHandlers,
  ...notificationHubHandlers,
  ...notificationHubInsightsHandlers,
  ...idManagerHandlers,
  ...ingestionHandlers,
  getGetCommentForSubscriptionMockHandler(),
  getTrackAnalyticsEventMockHandler(),
];

/**
 * Demo-dataset handlers for the browser dev worker (yarn msw:start:*) ONLY.
 * Deliberately NOT part of customHandlers: the f2f fixture builds its
 * playwright-msw worker from customHandlers, and playwright-msw lets these
 * baseline handlers shadow the per-spec generated-mock overrides the investor
 * f2f suites rely on (unlike msw's browser/node workers, where runtime
 * overrides win).
 */
export const demoOnlyHandlers = [
  ...homeLayoutDemoHandlers,
  ...quickLinksDemoHandlers,
  getWorkbenchInvestorsDemoHandler(),
  getWorkbenchInvestorByIdDemoHandler(),
  getUserActivityStatsDemoHandler(),
  getDemoFeatureFlagHandler(),
];
